import type { PersonalBestRecordItem } from '../services/api';
import type { PeakCategory, RecordMetricType } from './recordHistoryEnums';
import { formatPaceSeconds } from './paceZoneHelpers';

export const COACH_ATHLETE_COLORS = [
  '#4A90D9',
  '#7C3AED',
  '#3CC67D',
  '#F5B740',
  '#EA4782',
  '#29D4C6',
  '#a78bfa',
  '#f97316',
  '#16a34a',
  '#6366f1',
  '#0ea5e9',
  '#db2777',
  '#84cc16',
  '#eab308',
  '#64748b',
  '#14b8a6',
  '#c026d3',
  '#ef4444',
  '#22c55e',
  '#8b5cf6',
] as const;

export interface PeakTrendPoint {
  x: number;
  y: number;
  recordId: number;
  athleteId: number;
  athleteName: string;
  color: string;
  workoutId: number | null;
  workoutTitle: string | null;
  workoutDay: string;
  sportName: string | null;
}

export interface PeakTrendSeries {
  athleteId: number;
  athleteName: string;
  color: string;
  points: PeakTrendPoint[];
}

export function athleteTrendColor(athleteId: number, index: number): string {
  return COACH_ATHLETE_COLORS[index % COACH_ATHLETE_COLORS.length] ?? COACH_ATHLETE_COLORS[0];
}

export function recordMetricValue(
  record: PersonalBestRecordItem,
  metricType: RecordMetricType,
  isPowerPeak: boolean,
): number {
  if (metricType === 'wkg' && isPowerPeak) {
    const wkg = Number(record.wkg);
    if (wkg && !Number.isNaN(wkg)) return wkg;
  }
  const value = Number(record.value);
  return Number.isNaN(value) ? 0 : value;
}

export interface PeakTrendAthleteMeta {
  athleteId: number;
  athleteName: string;
  color: string;
}

export function mapRecordsToSeriesPoints(
  records: PersonalBestRecordItem[],
  metricType: RecordMetricType,
  isPowerPeak: boolean,
  meta: PeakTrendAthleteMeta,
): PeakTrendPoint[] {
  return records
    .map((r) => {
      const y = recordMetricValue(r, metricType, isPowerPeak);
      const x = new Date(r.record_date).getTime();
      if (!y || !x || Number.isNaN(x)) return null;
      return {
        x,
        y,
        recordId: r.id,
        athleteId: meta.athleteId,
        athleteName: meta.athleteName,
        color: meta.color,
        workoutId: r.workout?.id ?? r.workout_id ?? null,
        workoutTitle: r.workout?.title ?? null,
        workoutDay: r.workout?.workout_day ?? r.record_date,
        sportName: r.workout?.workout_type?.name ?? null,
      };
    })
    .filter((p): p is PeakTrendPoint => !!p)
    .sort((a, b) => a.x - b.x);
}

/** Client-side guard when API returns rows outside the requested ISO date window. */
export function clipPointsToIsoRange(
  points: PeakTrendPoint[],
  startIso?: string,
  endIso?: string,
): PeakTrendPoint[] {
  if (!startIso || !endIso) return points;
  const startMs = new Date(`${startIso}T00:00:00`).getTime();
  const endMs = new Date(`${endIso}T23:59:59.999`).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return points;
  return points.filter((p) => p.x >= startMs && p.x <= endMs);
}

/** All visible peaks in chronological order — used for bottom-sheet carousel. */
export function flattenVisiblePeakPoints(
  series: PeakTrendSeries[],
  visibleIds: Set<number>,
): PeakTrendPoint[] {
  return series
    .filter((s) => visibleIds.has(s.athleteId))
    .flatMap((s) => s.points)
    .sort((a, b) => a.x - b.x || a.athleteName.localeCompare(b.athleteName));
}

export function indexOfPeakPoint(
  points: PeakTrendPoint[],
  athleteId: number,
  recordId: number,
): number {
  return points.findIndex((p) => p.athleteId === athleteId && p.recordId === recordId);
}

/** Calendar day key (local) for grouping peaks on the same date. */
export function peakCalendarDayKey(ms: number): string {
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Same athlete, same calendar day — for bottom sheet when tapping a chart point. */
export function collectSameDayPointsForAthlete(
  series: PeakTrendSeries[],
  visibleIds: Set<number>,
  tapped: PeakTrendPoint,
): PeakTrendPoint[] {
  const dayKey = peakCalendarDayKey(tapped.x);
  if (!dayKey) return [tapped];

  const sameDay = series
    .filter((s) => visibleIds.has(s.athleteId) && s.athleteId === tapped.athleteId)
    .flatMap((s) => s.points)
    .filter((p) => peakCalendarDayKey(p.x) === dayKey)
    .sort((a, b) => b.y - a.y || a.recordId - b.recordId);

  return sameDay.length > 0 ? sameDay : [tapped];
}

/** Lighten athlete line color for PMC-style horizontal gradient strokes. */
export function lightenAthleteLineColor(hex: string, amount = 0.28): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return hex;
  const parse = (start: number) => parseInt(h.slice(start, start + 2), 16);
  const mix = (c: number) => Math.min(255, Math.round(c + (255 - c) * amount));
  const r = mix(parse(0));
  const g = mix(parse(2));
  const b = mix(parse(4));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export function buildPeakTrendLayout(
  series: PeakTrendSeries[],
  width: number,
  height: number,
  pad = { top: 14, right: 10, bottom: 26, left: 38 },
) {
  const plotW = Math.max(1, width - pad.left - pad.right);
  const plotH = Math.max(1, height - pad.top - pad.bottom);

  const allPoints = series.flatMap((s) => s.points);
  if (!allPoints.length) {
    return null;
  }

  const xs = allPoints.map((p) => p.x);
  const ys = allPoints.map((p) => p.y);
  const rawMinX = Math.min(...xs);
  const rawMaxX = Math.max(...xs);
  const rawMinY = Math.min(...ys);
  const rawMaxY = Math.max(...ys);
  const singleTimestamp = rawMinX === rawMaxX;
  const singleValue = rawMinY === rawMaxY;
  const halfDayMs = 12 * 60 * 60 * 1000;
  const minX = singleTimestamp ? rawMinX - halfDayMs : rawMinX;
  const maxX = singleTimestamp ? rawMaxX + halfDayMs : rawMaxX;
  const spanX = maxX - minX;
  const rawSpanY = rawMaxY - rawMinY;
  const yPad = singleValue
    ? Math.max(Math.abs(rawMinY) * 0.1, 1)
    : Math.max(rawSpanY * 0.08, 0.1);
  const minY = rawMinY - yPad;
  const maxY = rawMaxY + yPad;
  const spanY = maxY - minY;

  const xScale = (x: number) => pad.left + ((x - minX) / spanX) * plotW;
  const yScale = (y: number) =>
    pad.top + plotH - ((y - minY) / spanY) * plotH;

  return { minX, maxX, minY, maxY, xScale, yScale, pad, plotW, plotH };
}

/** Keep the strongest peaks in the selected window, then restore chronological chart order. */
export function selectTopPeakPoints(
  points: PeakTrendPoint[],
  category: PeakCategory,
  limit = 10,
): PeakTrendPoint[] {
  const ranked = [...points].sort((a, b) =>
    category === 'pace' ? a.y - b.y || b.x - a.x : b.y - a.y || b.x - a.x,
  );
  return ranked.slice(0, limit).sort((a, b) => a.x - b.x || a.recordId - b.recordId);
}

export function polylineFromPoints(
  points: PeakTrendPoint[],
  xScale: (x: number) => number,
  yScale: (y: number) => number,
): string {
  return points.map((p) => `${xScale(p.x)},${yScale(p.y)}`).join(' ');
}

export function nearestPointsAtX(
  series: PeakTrendSeries[],
  hoverX: number,
): { athleteId: number; athleteName: string; color: string; y: number; dateMs: number }[] {
  const hits: { athleteId: number; athleteName: string; color: string; y: number; dateMs: number; dist: number }[] =
    [];

  for (const s of series) {
    if (!s.points.length) continue;
    let best = s.points[0];
    let bestDist = Math.abs(best.x - hoverX);
    for (const p of s.points) {
      const d = Math.abs(p.x - hoverX);
      if (d < bestDist) {
        best = p;
        bestDist = d;
      }
    }
    hits.push({
      athleteId: s.athleteId,
      athleteName: s.athleteName,
      color: s.color,
      y: best.y,
      dateMs: best.x,
      dist: bestDist,
    });
  }

  return hits.sort((a, b) => b.y - a.y);
}

export function formatPeakTrendValue(y: number, category: PeakCategory): string {
  if (category === 'pace') return formatPaceSeconds(y);
  return String(Math.round(y));
}

export function peakTrendUnit(category: PeakCategory, metricType: RecordMetricType): string {
  if (category === 'pace') return '/km';
  if (category === 'heart_rate') return 'bpm';
  return metricType === 'wkg' ? 'W/kg' : 'W';
}
